import { useState } from "react";
import { Loader2, Square } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Hint } from "@/components/ui/tooltip";
import { api, ApiError } from "@/lib/api";
import { useCan } from "@/lib/auth";

/**
 * Stop for one run, named by its turn id — a parallel run beside the agent's main
 * session, which the chat's Stop does not reach. The API only records the ask;
 * the worker kills the run within a second or two, so it reads "Stopping" until
 * the run is gone from the list.
 */
export function RunStopButton({
  agentId,
  turnId,
  label,
  stopping: stopRequested = false,
  onStopped,
}: {
  agentId: string;
  turnId: string;
  /** What the run is, for the toast. */
  label: string;
  stopping?: boolean;
  onStopped?: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  // Stopping an agent is admin work; the server refuses it for anyone else.
  const canStop = useCan("admin");
  const stopping = pressed || stopRequested;
  if (!canStop) return null;
  const stop = async () => {
    setPressed(true);
    try {
      const { stoppedTurnId } = await api.stopAgentRun(agentId, turnId);
      if (!stoppedTurnId) setPressed(false);
      toast.success(stoppedTurnId ? `Stopping ${label}` : `${label} had already finished`);
      onStopped?.();
    } catch (cause) {
      setPressed(false);
      toast.error(cause instanceof ApiError ? cause.message : `Could not stop ${label}`);
    }
  };
  return (
    <Hint label={stopping ? "Stopping…" : "Stop this run"}>
      <Button
        variant="ghost"
        size="icon-sm"
        className="shrink-0 text-destructive hover:text-destructive"
        disabled={stopping}
        onClick={(event) => {
          event.stopPropagation();
          void stop();
        }}
        aria-label={stopping ? "Stopping" : `Stop ${label}`}
      >
        {stopping ? <Loader2 className="size-3.5 animate-spin" /> : <Square className="size-3.5 fill-current" />}
      </Button>
    </Hint>
  );
}
